import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import type * as am4core from "@amcharts/amcharts4/core";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import {
  createDateAxis,
  createValueAxis,
  type OneSeriesTrendProperty,
} from "../TrendsUtils";
import useTrend from "../useTrend";
import type { SelectedScope } from "../../../../types/scope";

// Single point in the chart series, derived from each `[date, value]` tuple
// returned by the trend query.
interface OneSeriesChartDatum {
  date: string;
  value: number;
  dashLength?: number;
}

// Raw trend rows are `[date, value]` tuples coming from parsed JSON; trendData
// from useTrend is typed `unknown`, so we narrow to this shape locally.
type TrendRow = [string, number];

export interface OneSeriesByDiscoveryDateProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  timeScale: string;
  chartData: OneSeriesTrendProperty;
  id: string | number;
  widgetKey?: string;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const OneSeriesByDiscoveryDate: React.FC<OneSeriesByDiscoveryDateProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  timeScale,
  chartData,
  id,
  widgetKey,
  setIsReadyToExport = null,
}) => {
  const [trendData, setTrendData] = useState<OneSeriesChartDatum[] | null>(
    null
  );
  const [max, setMax] = useState(0);
  const chartName = `${chartData?.title?.replaceAll(/\s/g, "")}Trend-${id}`;
  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport);
  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      selection: chartData.queryNameVariable,
      timescale: timeScale,
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: ["actual_status"],
      },
      aggregate_functions: ["SUM"],
      round_functions: ["ROUND"],
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const rows = data as TrendRow[];
      let findingsByTimeScale: OneSeriesChartDatum[] = [];
      findingsByTimeScale = rows.map((timeScale, index) => {
        const dataVal: OneSeriesChartDatum = {
          date: timeScale[0],
          value: timeScale[1],
        };

        if (rows?.length >= 2 && index === rows?.length - 2) {
          dataVal.dashLength = 8;
        }
        return dataVal;
      });

      if (
        !findingsByTimeScale?.length ||
        !findingsByTimeScale?.find((item) => item.value !== 0)
      ) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (findingsByTimeScale?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      let maxNum = 0;
      rows.forEach((item) => {
        item?.forEach((value) => {
          if ((value as number) > maxNum) {
            maxNum = value as number;
          }
        });
      });

      setMax(maxNum * 1.1);
      setTrendData(findingsByTimeScale);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      stopLoadingChart();

      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);

      chart.cursor = new am4charts.XYCursor();
      chart.paddingTop = 45;
      chart.paddingRight = 40;
      chart.paddingBottom = -10;

      // Preserve runtime behavior: original assigned `trendData` directly,
      // which may be null; amCharts tolerates it. `data` is typed `any[]`.
      chart.data = trendData as OneSeriesChartDatum[];

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Findings", max);

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = "value";
      series.dataFields.dateX = "date";
      series.tooltipText = "{name}: {value}";
      series.tooltip!.autoTextColor = false;
      // Preserve runtime behavior: original assigned the string "white";
      // amCharts coerces it at runtime, but the typed setter expects a Color.
      series.tooltip!.label.fill = "white" as unknown as am4core.Color;
      series.strokeWidth = 2;
      series.minBulletDistance = 15;
      series.tensionX = 0.8;
      series.name = chartData.legend;
      series.propertyFields.strokeDasharray = "dashLength";
      series.showOnInit = true;

      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.strokeWidth = 2;
      bullet.circle.radius = 3;

      const bulletHover = bullet.states.create("hover");
      bulletHover.properties.scale = 1.3;

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [trendData, dataExists, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchTrend}
    />
  );
};

export default OneSeriesByDiscoveryDate;
